// ========================================
// PAGE ARTICLE
// Détail d'un article
// ========================================

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navigation from '../../components/common/Navigation';
import Footer from '../../components/common/Footer';
import LoadingScreen from '../../components/common/LoadingScreen';
import { useBlogPosts, useSiteSettings } from '../../hooks';
import { 
  FiArrowLeft, 
  FiCalendar, 
  FiClock, 
  FiEye,
  FiHeart,
  FiArrowRight,
  FiUser,
  FiAlertCircle,
  FiLink,
  FiCheck
} from 'react-icons/fi';
import { FaTwitter, FaLinkedin, FaFacebook, FaWhatsapp } from 'react-icons/fa';
import './Article.css';

// ============================================
// COULEURS DES CATÉGORIES
// ============================================
const CATEGORY_COLORS = {
  communication: '#3B82F6',
  leadership: '#10B981',
  ecriture: '#8B5CF6',
  opportunites: '#F59E0B',
  technologie: '#EF4444',
  culture: '#EC4899',
  education: '#14B8A6',
  autre: '#6B7280'
};

const CATEGORY_LABELS = {
  communication: 'Communication',
  leadership: 'Leadership',
  ecriture: 'Écriture',
  opportunites: 'Opportunités',
  technologie: 'Technologie',
  culture: 'Culture',
  education: 'Éducation',
  autre: 'Autre'
};

// ============================================
// ✅ FONCTION DE NETTOYAGE DU CONTENU HTML
// Nettoie les problèmes générés par Quill
// ============================================
const cleanHtmlContent = (html) => {
  if (!html) return '';
  
  let cleaned = html;
  
  // 1. Remplacer tous les &nbsp; par des espaces normaux
  cleaned = cleaned.replace(/&nbsp;/g, ' ');
  
  // 2. Remplacer les caractères Unicode d'espace insécable
  cleaned = cleaned.replace(/\u00A0/g, ' ');
  
  // 3. Supprimer les multiples espaces consécutifs (garder un seul)
  cleaned = cleaned.replace(/  +/g, ' ');
  
  // 4. Remplacer ql-align-justify par ql-align-left dans les classes
  cleaned = cleaned.replace(/ql-align-justify/g, 'ql-align-left');
  
  // 5. Supprimer style="text-align: justify" inline
  cleaned = cleaned.replace(/style\s*=\s*["'][^"']*text-align\s*:\s*justify[^"']*["']/gi, '');
  
  return cleaned;
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================
const Article = () => {
  const { slug } = useParams();

  // ========== HOOKS FIREBASE ==========
  const { posts, loading, getPostBySlug, incrementViews, toggleLike } = useBlogPosts(true, 'published');
  const { settings } = useSiteSettings();

  // ========== ÉTATS LOCAUX ==========
  const [article, setArticle] = useState(null);
  const [articleLoading, setArticleLoading] = useState(true);
  const [error, setError] = useState(null);
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [copied, setCopied] = useState(false);
  const [viewIncremented, setViewIncremented] = useState(false);

  // ========== ARTICLES SIMILAIRES ==========
  const relatedArticles = useMemo(() => {
    if (!posts?.length || !article) return [];

    return posts
      .filter(post => 
        post.id !== article.id && 
        (post.category === article.category || 
         post.tags?.some(tag => article.tags?.includes(tag)))
      )
      .slice(0, 2);
  }, [posts, article]);

  // ========== CHARGER L'ARTICLE ==========
  useEffect(() => {
    const loadArticle = async () => {
      setArticleLoading(true);
      setError(null);

      try {
        // D'abord chercher dans les posts déjà chargés
        if (posts?.length) {
          const found = posts.find(p => p.slug === slug);
          if (found) {
            setArticle(found);
            setLikesCount(found.likes || 0);
            setArticleLoading(false);
            return;
          }
        }

        // Sinon, charger depuis Firebase
        const result = await getPostBySlug(slug);
        if (result.success && result.data) {
          setArticle(result.data);
          setLikesCount(result.data.likes || 0);
        } else {
          setError('Article non trouvé');
        }
      } catch (err) {
        console.error('Erreur chargement article:', err);
        setError('Erreur lors du chargement de l\'article');
      }

      setArticleLoading(false);
    };

    if (slug) {
      loadArticle();
    }
  }, [slug, posts, getPostBySlug]);

  // ========== INCRÉMENTER LES VUES ==========
  useEffect(() => {
    const incrementViewCount = async () => {
      if (article?.id && !viewIncremented) {
        try {
          await incrementViews(article.id);
          setViewIncremented(true);
        } catch (err) {
          console.error('Erreur incrémentation vues:', err);
        }
      }
    };

    incrementViewCount();
  }, [article?.id, viewIncremented, incrementViews]);

  // ========== SCROLL TO TOP ==========
  useEffect(() => {
    window.scrollTo(0, 0);
    // Reset view incremented state when slug changes
    setViewIncremented(false);
  }, [slug]);

  // ========== HELPERS ==========
  const formatDate = (dateValue) => {
    if (!dateValue) return '';
    
    const date = dateValue?.toDate ? dateValue.toDate() : new Date(dateValue);
    const options = { day: 'numeric', month: 'long', year: 'numeric' };
    return date.toLocaleDateString('fr-FR', options);
  };

  const getCategoryColor = (category) => {
    return CATEGORY_COLORS[category] || '#6B7280';
  };

  const getCategoryLabel = (category) => {
    return CATEGORY_LABELS[category] || category || 'Autre';
  };

  const getShareUrl = () => window.location.href;
  
  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(getShareUrl())}&text=${encodeURIComponent(article?.title || '')}`,
    linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(getShareUrl())}&title=${encodeURIComponent(article?.title || '')}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareUrl())}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${article?.title || ''} - ${getShareUrl()}`)}`
  };

  const handleLike = async () => {
    if (!article?.id) return;

    try {
      // Optimistic update
      if (liked) {
        setLikesCount(prev => Math.max(0, prev - 1));
      } else {
        setLikesCount(prev => prev + 1);
      }
      setLiked(!liked);

      // Appeler Firebase
      await toggleLike(article.id, !liked);
    } catch (err) {
      // Rollback en cas d'erreur
      if (liked) {
        setLikesCount(prev => prev + 1);
      } else {
        setLikesCount(prev => Math.max(0, prev - 1));
      }
      setLiked(liked);
      console.error('Erreur like:', err);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Erreur copie:', err);
    }
  };

  const getAuthorInitials = () => {
    const name = article?.author || settings?.siteName || 'GK';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  // ========== RENDU LOADING ==========
  if (loading || articleLoading) {
    return <LoadingScreen message="Chargement de l'article..." />;
  }

  // ========== RENDU ERREUR / NOT FOUND ==========
  if (error || !article) {
    return (
      <div className="article-page">
        <Navigation />
        <div className="not-found-container">
          <motion.div
            className="not-found-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="not-found-icon">
              <FiAlertCircle />
            </div>
            <h1>Article non trouvé</h1>
            <p>L'article que vous recherchez n'existe pas ou a été supprimé.</p>
            <Link to="/blog" className="btn btn-primary">
              <FiArrowLeft />
              Retour au blog
            </Link>
          </motion.div>
        </div>
        <Footer />
      </div>
    );
  }

  // ✅ Nettoyer le contenu avant l'affichage
  const cleanedContent = cleanHtmlContent(article.content);

  // ========== RENDU PRINCIPAL ==========
  return (
    <div className="article-page">
      <Navigation />

      {/* Hero */}
      <section className="article-hero">
        <div className="hero-background">
          {article.coverImage ? (
            <img src={article.coverImage} alt={article.title} />
          ) : (
            <div className="hero-gradient"></div>
          )}
          <div className="hero-overlay"></div>
        </div>

        <div className="container">
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/blog" className="back-link">
              <FiArrowLeft />
              Retour au blog
            </Link>

            <span 
              className="article-category"
              style={{ backgroundColor: getCategoryColor(article.category) }}
            >
              {getCategoryLabel(article.category)}
            </span>

            <h1>{article.title}</h1>

            <div className="article-meta">
              <div className="author-info">
                <div className="author-avatar">
                  {article.authorImage ? (
                    <img src={article.authorImage} alt={article.author} />
                  ) : (
                    <FiUser />
                  )}
                </div>
                <span className="author-name">
                  {article.author || settings?.siteName || 'Gustave KONATE'}
                </span>
              </div>
              
              <div className="meta-divider"></div>
              
              <span className="meta-item">
                <FiCalendar />
                {formatDate(article.publishedAt)}
              </span>
              
              <span className="meta-item">
                <FiClock />
                {article.readTime || 5} min de lecture
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="article-content-section">
        <div className="container">
          <div className="article-layout">
            {/* Sidebar - Share */}
            <aside className="article-sidebar">
              <div className="sidebar-sticky">
                <div className="share-section">
                  <span className="share-label">Partager</span>
                  <div className="share-buttons">
                    <a 
                      href={shareLinks.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="share-btn twitter"
                      aria-label="Partager sur Twitter"
                    >
                      <FaTwitter />
                    </a>
                    <a 
                      href={shareLinks.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="share-btn linkedin"
                      aria-label="Partager sur LinkedIn"
                    >
                      <FaLinkedin />
                    </a>
                    <a 
                      href={shareLinks.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="share-btn facebook"
                      aria-label="Partager sur Facebook"
                    >
                      <FaFacebook />
                    </a>
                    <a 
                      href={shareLinks.whatsapp}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="share-btn whatsapp"
                      aria-label="Partager sur WhatsApp"
                    >
                      <FaWhatsapp />
                    </a>
                    <button 
                      className={`share-btn copy ${copied ? 'copied' : ''}`}
                      onClick={handleCopyLink}
                      aria-label="Copier le lien"
                    >
                      {copied ? <FiCheck /> : <FiLink />}
                    </button>
                  </div>
                </div>

                <div className="stats-section">
                  <div className="stat-item">
                    <FiEye />
                    <span>{(article.views || 0) + (viewIncremented ? 1 : 0)} vues</span>
                  </div>
                  <button 
                    className={`like-button ${liked ? 'liked' : ''}`}
                    onClick={handleLike}
                  >
                    <FiHeart />
                    <span>{likesCount}</span>
                  </button>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <motion.article
              className="article-main"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {/* ✅ Contenu nettoyé */}
              <div 
                className="article-body"
                dangerouslySetInnerHTML={{ __html: cleanedContent }}
              />

              {/* Tags */}
              {article.tags?.length > 0 && (
                <div className="article-tags">
                  {article.tags.map(tag => (
                    <span key={tag} className="tag">#{tag}</span>
                  ))}
                </div>
              )}

              {/* Author Box */}
              <div className="author-box">
                <div className="author-avatar-large">
                  {article.authorImage ? (
                    <img src={article.authorImage} alt={article.author} />
                  ) : (
                    <span>{getAuthorInitials()}</span>
                  )}
                </div>
                <div className="author-details">
                  <span className="author-label">Écrit par</span>
                  <h4>{article.author || settings?.siteName || 'Gustave KONATE'}</h4>
                  <p>Communicant, écrivain et leader associatif passionné par le développement de l'Afrique.</p>
                  <Link to="/a-propos" className="author-link">
                    En savoir plus <FiArrowRight />
                  </Link>
                </div>
              </div>
            </motion.article>
          </div>
        </div>
      </section>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="related-section">
          <div className="container">
            <h2>Articles similaires</h2>
            <div className="related-grid">
              {relatedArticles.map(relatedArticle => (
                <motion.article
                  key={relatedArticle.id}
                  className="related-card"
                  whileHover={{ y: -5 }}
                >
                  <Link to={`/blog/${relatedArticle.slug}`}>
                    <div className="related-image">
                      {relatedArticle.coverImage ? (
                        <img src={relatedArticle.coverImage} alt={relatedArticle.title} />
                      ) : (
                        <div className="related-placeholder">
                          <span>{relatedArticle.title?.charAt(0) || 'A'}</span>
                        </div>
                      )}
                      <span 
                        className="related-category"
                        style={{ backgroundColor: getCategoryColor(relatedArticle.category) }}
                      >
                        {getCategoryLabel(relatedArticle.category)}
                      </span>
                    </div>
                    <div className="related-content">
                      <h3>{relatedArticle.title}</h3>
                      <span className="related-meta">
                        <FiClock /> {relatedArticle.readTime || 5} min
                      </span>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default Article;